import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { proprietesLien } from '../services/fichiers';

/**
 * Les comptes rendus déjà rendus sur ce dossier.
 *
 * Une commande revient sous la même référence O avec une référence W nouvelle.
 * Le technicien qui repasse a besoin de lire ce que le précédent a constaté —
 * une chambre encombrée, un PBO saturé, un percement refusé — et il n'avait
 * aucun moyen d'y accéder depuis le terrain. Il appelait, ou il refaisait le
 * constat lui-même.
 */

const COULEUR = {
  CLEARED: ['var(--succes-fond)', 'var(--succes-texte)', 'Réalisé'],
  FAILED: ['var(--echec-fond)', 'var(--echec-texte)', 'Échec'],
};

function jour(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function HistoriqueCRI() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    api.get(`/cr/historique/${id}`)
      .then(r => setListe(r.data.comptes_rendus || []))
      .catch(e => setErreur(e.response?.data?.error || 'Lecture impossible'));
  }, [id]);

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={() => navigate(`/dossier/${id}`)}>‹</div>
        <div className="topbar-title" style={{ fontSize: 15 }}>Historique des CR</div>
      </div>

      <div className="content">
        {erreur && <div className="section-card" style={{ color: 'var(--echec-texte)' }}>{erreur}</div>}
        {!liste && !erreur && (
          <div className="section-card" style={{ color: 'var(--texte-doux)' }}>Lecture…</div>
        )}

        {liste && liste.length === 0 && (
          <div className="section-card">
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
              Aucun compte rendu sur ce dossier. C’est le premier passage.
            </div>
          </div>
        )}

        {liste && liste.map(cr => {
          const [fond, texte, label] = COULEUR[cr.resultat] || ['var(--surface-2)', 'var(--texte-doux)', cr.resultat || '—'];
          return (
            <div key={cr.id} className="section-card" style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 12,
                               background: fond, color: texte }}>{cr.type_cr} · {label}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{cr.ref_work_order || '—'}</span>
              </div>

              <div style={{ fontSize: 13, color: 'var(--texte-doux)', lineHeight: 1.6 }}>
                {jour(cr.date)}
                {cr.technicien_nom ? <> · {cr.technicien_nom}</> : null}
                {cr.type_raccordement ? <> · {cr.type_raccordement}</> : null}
              </div>

              {/* La cause de l'echec est l'information la plus utile du lot :
                  elle dit ce qui attend celui qui repasse. */}
              {cr.type_echec && (
                <div style={{ marginTop: 6, padding: '7px 10px', borderRadius: 8,
                              background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)',
                              fontSize: 13, color: 'var(--attention-texte)', lineHeight: 1.45 }}>
                  {cr.type_echec}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                {cr.pdf_url && (
                  <a {...proprietesLien(cr.pdf_url)}
                    style={{ fontSize: 13, fontWeight: 600, padding: '7px 13px', borderRadius: 9,
                             background: 'var(--surface-2)', border: '0.5px solid var(--bord-fort)',
                             color: 'var(--texte)', textDecoration: 'none' }}>
                    Ouvrir le compte rendu
                  </a>
                )}
                {cr.devis_xlsx_url && (
                  <a {...proprietesLien(cr.devis_xlsx_url)}
                    style={{ fontSize: 13, fontWeight: 600, padding: '7px 13px', borderRadius: 9,
                             background: 'var(--surface-2)', border: '0.5px solid var(--bord-fort)',
                             color: 'var(--texte)', textDecoration: 'none' }}>
                    Devis
                  </a>
                )}
              </div>
            </div>
          );
        })}

        {liste && liste.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--texte-doux)', padding: '0 4px', lineHeight: 1.5 }}>
            {liste.length} compte{liste.length > 1 ? 's' : ''} rendu{liste.length > 1 ? 's' : ''} sur cette commande,
            du plus récent au plus ancien.
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}><span className="nav-icon">📅</span>Planning</div>
        <div className="nav-item" onClick={() => navigate(`/dossier/${id}`)}><span className="nav-icon">📁</span>Dossier</div>
        <div className="nav-item" onClick={() => navigate(`/pj/${id}`)}><span className="nav-icon">📎</span>PJ</div>
        <div className="nav-item active"><span className="nav-icon">🕘</span>Historique</div>
      </div>
    </div>
  );
}
